import { attribute, tagName } from "@ember-decorators/component"
import Component from "@ember/component"
import { computed, set } from "@ember/object"
import { scheduleOnce } from "@ember/runloop"
import type { AiValueEditor } from "cc-frontend/lib/ai/ai-value-editor"

@tagName("div")
export default class SimpleContentEditable extends Component implements AiValueEditor<string> {
  // Args
  declare value: string
  declare class: string
  declare onValueUpdate: Function
  declare onFinishedEditing: Function
  declare isEditable: boolean
  declare onClick: Function
  declare onFocusIn: Function
  declare onFocusOut: Function

  // Properties
  _value
  tagName = "div"
  isUserTyping = false
  isAiEditing = false
  plaintext = true
  // placeholder = "Title"
  registerEditor?: (editor: AiValueEditor<unknown>) => void
  focusInWordCount
  originalValue
  onKeyDown?

  // @attribute()
  // contenteditable

  @attribute() placeholder = "Title"

  // Lets you focusIn, even if not content editable
  @attribute() tabindex = 0

  // init
  didInsertElement(): void {
    this.registerEditor?.(this)
  }

  didReceiveAttrs() {
    if (this.isUserTyping) return
    if (this.isAiEditing) return
    if (this.value !== this._value) {
      // @ts-ignore

      scheduleOnce("afterRender", () => $(this.element).text(this._value) || "")
      set(this, "_value", this.value)
    }
  }

  // Cps/getters
  @attribute()
  @computed("this.isEditable")
  get contentEditable() {
    var editable = this.isEditable
    return editable ? true : false
  }

  // Actions
  mouseLeave() {
    if (this.isAiEditing) return

    this.set("isUserTyping", false)
  }

  focusIn() {
    if (this.isAiEditing) return

    var text = $(this.element).text()
    set(this, "originalValue", text)

    var wordCount = 0
    // if there are words
    if (text.length > 0) {
      wordCount = text.split(" ").length
    }

    this.set("focusInWordCount", wordCount)
    this.set("isUserTyping", true)
    if (this.onFocusIn) this.onFocusIn()
    return true
  }

  focusOut() {
    if (this.isAiEditing) return

    this.set("isUserTyping", false)
    if (this.onFocusOut) this.onFocusOut()
    if (!this.isEditable) return

    var text = $(this.element).text()
    let focusOutWordCount
    if (text.length > 0) focusOutWordCount = text.split(" ").length
    let wordCountChange = focusOutWordCount - this.focusInWordCount

    if (text !== this.originalValue) {
      this.onFinishedEditing(text, this.originalValue, wordCountChange)
    }

    // we've gotten value updates, so once we focus out, we set those
    set(this, "_value", this.value)

    // Ugh. This prevents a weird double text display where it looks like {{_value}}{{_value}}. Presumably,
    // Ember is getting confused by the text typed in and {{_value}}. This line straightens that out
    //@ts-ignore - asking for 3 args, works with 2

    scheduleOnce("afterRender", () => {
      if ($(this.element)) $(this.element).text(this._value) || ""
    })

    return true
  }

  keyDown(event) {
    if (this.isAiEditing) return

    if (!event.metaKey) {
      this.set("isUserTyping", true)
    }
    // prevent enter key
    if (event.keyCode === 13 || event.which === 13) {
      event.preventDefault()
      return true
    }

    if (this.onKeyDown) this.onKeyDown(event)

    return true
  }

  keyUp(event) {
    if (this.isAiEditing) return

    // this.set('_value', $(this.element).text())
    if (!this.isEditable) return
    this.notifyValueUpdate()
  }

  input() {
    if (this.isAiEditing) return
    if (!this.isEditable) return
    this.notifyValueUpdate()
  }

  private notifyValueUpdate() {
    if (!this.onValueUpdate) return

    let newValue = $(this.element).text()
    if (newValue === "" || newValue !== this.originalValue) {
      this.onValueUpdate(newValue)
    }
  }

  drop(event) {
    event.preventDefault()
  }

  click(event) {
    if (this.isAiEditing) return

    if (this.onClick) this.onClick(event)
  }

  paste(e) {
    if (this.isAiEditing) {
      e.preventDefault()
      return
    }

    var pastedText
    e.preventDefault()
    if (!this.isEditable) return
    //@ts-ignore - ts can't type window?
    if (window.clipboardData && window.clipboardData.getData) {
      // IE
      //@ts-ignore
      pastedText = window.clipboardData.getData("URL")
    } else if (e.originalEvent.clipboardData && e.originalEvent.clipboardData.getData) {
      pastedText = e.originalEvent.clipboardData.getData("text/plain")
    }
    document.execCommand("insertHTML", false, pastedText)
    this.set("_value", $(this.element).text())
    if (this.onValueUpdate) this.onValueUpdate($(this.element).text())
  }

  // AiValueEditor methods

  startAiEdits(): void {
    this.isAiEditing = true
  }

  setValueFromAi(aiValue: string): void {
    this.set("_value", aiValue)
    // Copied from focusOut().
    // @ts-ignore
    scheduleOnce("afterRender", () => {
      if ($(this.element)) $(this.element).text(this._value) || ""
    })
  }

  acceptAiEdits(): string {
    const result = this._value || ""
    this.isAiEditing = false
    // Copied from focusOut().
    // @ts-ignore
    scheduleOnce("afterRender", () => {
      if ($(this.element)) $(this.element).text(this._value) || ""
    })

    return result
  }

  rejectAiEdits(): void {
    if (!this.isAiEditing) return

    this.isAiEditing = false
    set(this, "_value", this.value)
    // Copied from focusOut().
    // @ts-ignore
    scheduleOnce("afterRender", () => {
      if ($(this.element)) $(this.element).text(this._value) || ""
    })
  }
}
